import { ATTRIBUTE_GROUP_DELIMITER } from "constants/product-collection";
import {
    DoorGroupParams,
    IDoorGroupsQueryParams,
    SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER,
    Order,
} from "helpers/door-collection";
import {
    getLegacyTypeByMetricsKind,
    getMetricKindByLegacyType,
    getTotalTypesLabel,
} from "helpers/metrics/legacy-metrics";
import { TotalTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { DimensionsPanelReduxState } from "../dimensions-panel";
import { safeParseInt } from "lib/number";

/**
 * "getDimensionsPanelStateFromUrl" attempts to parse URL param ?dg=<encoded state>
 * 1. If the param does not exists, or parsing fails, we return undefined
 * 2. If parsing succeeds, return the state
 *
 * e.g. "/.../shop?dg={"show":1}&mode=cart" => { show: 1 }
 *
 * TODO: check if state keys are valid
 */
export const getDimensionsPanelStateFromUrl = () => {
    try {
        const params = new URLSearchParams(location.search);
        const parsedDoorGroup = JSON.parse(params.get("dg"));
        return {
            showDoors: parsedDoorGroup.s === 0 ? false : true,
        };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Initial state could not be parsed from URL");
    }
};

/**
 * Tries to parse the URL parameters and return a state object. If it fails, it returns undefined
 */
export const getMetricsStateFromUrl = () => {
    try {
        const params = new URLSearchParams(location.search);
        const parsedDoorGroup = JSON.parse(params.get("dg"));
        return {
            showAssortmentTotals: parsedDoorGroup.showAssortmentTotals ? true : false,
        };
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Initial state could not be parsed from URL");
    }
};

export interface IDimensionsPanelToQueryOptions {
    legacyMetrics?: boolean;
}
/**
 * Convert dimensions state into query params.
 * This also omits out certain properties from being stored into URL
 * Inverse of `mergeQueryParams`
 * @param dimensionState
 * @param options
 * @return
 */
export const dimensionsPanelToQueryParams = (
    dimensionState: DimensionsPanelReduxState,
    options: IDimensionsPanelToQueryOptions = {},
): IDoorGroupsQueryParams | null => {
    const query = {};

    if (dimensionState.showDoors) {
        query[DoorGroupParams.Show] = 1;
    }

    if (dimensionState.metrics.showAssortmentTotals) {
        query[DoorGroupParams.ShowTotals] = true;
    }

    if (dimensionState.metrics.showAssortmentPlan) {
        query[DoorGroupParams.ShowPlanMetrics] = true;

        // Legacy metrics are stored in query params
        if (options.legacyMetrics && dimensionState.metrics.values.length) {
            query[DoorGroupParams.PlanMetrics] = dimensionState.metrics.values.map((metric) =>
                getLegacyTypeByMetricsKind(metric.kind),
            );
        }
    }

    dimensionState.attributes.forEach((attribute) => {
        if (attribute.showLabelInHeader) {
            query[DoorGroupParams.ClassificationLabel] = attribute.id;
        }
    });

    if (dimensionState.collapsedGroupIds.length > 0) {
        const columnsExpand = dimensionState.collapsedGroupIds.reduce((acc, groupId) => {
            acc[groupId] = 0;
            return acc;
        }, {});

        query[DoorGroupParams.Expand] = columnsExpand;
    }

    const activeAttributes = dimensionState.attributes.filter((attribute) => attribute.selected);
    if (activeAttributes.length > 0) {
        query[DoorGroupParams.Classification] = activeAttributes
            .map(
                (activeAttribute) =>
                    `${activeAttribute.id}${SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER}${activeAttribute.sort}`,
            )
            .join(ATTRIBUTE_GROUP_DELIMITER);
    }

    return Object.keys(query).length > 0 ? query : null;
};

/**
 * Take dimension params from query params and merge it with dimension params.
 * This method mutates `dimensionParams`
 * Inverse of `dimensionsPanelToQueryParams`
 * @param dimensionParams
 * @param queryParams
 */
export const mergeQueryParams = (dimensionParams: DimensionsPanelReduxState, queryParams: IDoorGroupsQueryParams) => {
    if (queryParams[DoorGroupParams.Show] == 1) {
        dimensionParams.showDoors = true;
    }

    if (queryParams[DoorGroupParams.ShowTotals]) {
        dimensionParams.metrics.showAssortmentTotals = true;
    }

    if (queryParams[DoorGroupParams.ShowPlanMetrics]) {
        dimensionParams.metrics.showAssortmentPlan = true;
    }

    // Important! This is legacy metric
    (queryParams[DoorGroupParams.PlanMetrics] ?? []).forEach((legacyMetricType) => {
        const metricKind = getMetricKindByLegacyType(legacyMetricType as TotalTypes);

        if (metricKind) {
            dimensionParams.metrics.showAssortmentPlan = true;
            dimensionParams.metrics.values.push({
                showValue: true,
                showTotals: true,
                showSubtotals: true,
                kind: metricKind,
                displayName: getTotalTypesLabel(legacyMetricType as TotalTypes),
                isTargetMetric: false,
            });
        }
    });

    if (queryParams[DoorGroupParams.Classification]) {
        (queryParams[DoorGroupParams.Classification] ?? "")
            .split(ATTRIBUTE_GROUP_DELIMITER)
            .forEach((attributeString) => {
                const [attributeKey, attributeSort] = attributeString.split(
                    SR_CUSTOM_DOOR_GROUPING_DIRECTION_DELIMITER,
                );
                const attribute = dimensionParams.attributes.find((attribute) => attribute.id === attributeKey);

                if (attribute) {
                    attribute.selected = true;
                    attribute.values.forEach((value) => {
                        value.selected = true;
                    });
                    attribute.sort = (attributeSort ?? Order.Ascending) as Order;
                }
            });
    }

    if (queryParams[DoorGroupParams.ClassificationLabel]) {
        dimensionParams.attributes.forEach((attribute) => {
            attribute.showLabelInHeader = attribute.id === queryParams[DoorGroupParams.ClassificationLabel];
        });
    }

    if (queryParams[DoorGroupParams.ClassificationDir]) {
        dimensionParams.attributes.forEach((attribute) => {
            if (attribute.selected) {
                attribute.sort = queryParams[DoorGroupParams.ClassificationDir];
            }
        });
    }

    if (queryParams[DoorGroupParams.ClassificationHide]) {
        dimensionParams.attributes.forEach((attribute) => {
            if (attribute.selected) {
                attribute.values.forEach((attributeValue) => {
                    if (!queryParams[DoorGroupParams.ClassificationHide].includes(attributeValue.id)) {
                        attributeValue.selected = true;
                    } else {
                        attributeValue.selected = false;
                    }
                });
            }
        });
    }

    const expandedColumns = queryParams[DoorGroupParams.Expand];
    if (expandedColumns) {
        dimensionParams.collapsedGroupIds = Object.keys(expandedColumns).filter(
            (key) => !safeParseInt(expandedColumns[key], 1),
        );
    }
};
